import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ApprovalCreated,
  type ApprovalDetail,
  type ApprovalListQuery,
  type ApprovalListResponse,
  type ApprovalPendingCount,
  type BankChangeRequest,
  approvalCreatedSchema,
  approvalDetailSchema,
  approvalListResponseSchema,
  approvalPendingCountSchema,
} from '@thrivehrm/shared';
import { api } from '@/lib/api';
import { hasAnyPermission, useAuth } from '@/lib/auth';
import { queryKeys } from '@/lib/queryKeys';
import { APPROVER_PERMISSIONS } from './approvers';

type ListParams = Partial<ApprovalListQuery> & Pick<ApprovalListQuery, 'page' | 'pageSize'>;

/** `GET /approvals` (ADR-017): what this viewer may see. */
export function useApprovals(params: ListParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.approvals.list(params),
    queryFn: ({ signal }) =>
      api.get<ApprovalListResponse>('/approvals', {
        query: { ...params },
        schema: approvalListResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** `GET /approvals/:id` — steps and the requested change (values only when allowed). */
export function useApproval(id: string | null) {
  return useQuery({
    queryKey: queryKeys.approvals.detail(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<ApprovalDetail>(`/approvals/${id ?? ''}`, { schema: approvalDetailSchema, signal }),
    enabled: id !== null,
  });
}

/** `GET /approvals/pending-count` — requests waiting for me (menu and top-band badges). */
export function usePendingCount(enabled = true) {
  return useQuery({
    queryKey: queryKeys.approvals.pendingCount,
    queryFn: ({ signal }) =>
      api.get<ApprovalPendingCount>('/approvals/pending-count', {
        schema: approvalPendingCountSchema,
        signal,
      }),
    refetchInterval: 60_000,
    enabled,
  });
}

/** Requests waiting for this user's decision (FR-BANK-4, FR-LEAVE-5); 0 while loading. */
export function usePendingApprovalsCount(): number {
  const { user } = useAuth();
  const { data } = usePendingCount(
    (user?.hasReports ?? false) || hasAnyPermission(user, APPROVER_PERMISSIONS),
  );
  return data?.count ?? 0;
}

/** Approvals and the employee's data may both change after a request or a decision. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all }),
    ]);
  };
}

export function useApprovalAction(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (
      action:
        | { kind: 'approve'; effectiveDate?: string; note?: string }
        | { kind: 'reject'; reason: string }
        | { kind: 'cancel' },
    ) => {
      const { kind, ...body } = action;
      return api.post<ApprovalDetail>(`/approvals/${id}/${kind}`, body, {
        schema: approvalDetailSchema,
      });
    },
    onSuccess: invalidate,
  });
}

/** `POST /employees/:id/bank/requests` (FR-BANK-3), own record only. */
export function useRequestBankChange(employeeId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: BankChangeRequest) =>
      api.post<ApprovalCreated>(`/employees/${employeeId}/bank/requests`, body, {
        schema: approvalCreatedSchema,
      }),
    onSuccess: invalidate,
  });
}

/** `POST /approvals/:id/steps/:stepNo/reassign` (ADR-059): the type's reassigners only. */
export function useReassignStep(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ stepNo, userId }: { stepNo: number; userId: string }) =>
      api.post<ApprovalDetail>(
        `/approvals/${id}/steps/${String(stepNo)}/reassign`,
        { userId },
        { schema: approvalDetailSchema },
      ),
    onSuccess: invalidate,
  });
}
